import { useCallback, useMemo, useState } from "react";
import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { SortableTableHead } from "@trillion/ui/components/admin/sortable-header";
import { SORTABLE_GRID_OPTIONS, orderByKey } from "@trillion/ui/lib/data-grid-sort";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { useNavigate } from "react-router-dom";
import { getRecurringColumns } from "@/components/jobs/recurring-columns";
import { TriggerDialog } from "@/components/jobs/trigger-dialog";
import { GridFilterInput, NO_MATCHES_TEXT } from "@/components/grid-filter-input";
import { usePollWhile } from "@/hooks/use-poll-while";
import { POLL_INTERVAL_MS, useRecurringJobs } from "@/hooks/use-recurring-jobs";
import { shouldPoll } from "@/lib/job-states";
import { jobDetailsPath } from "@/lib/job-details";
import type { RecurringJob } from "@/lib/jobs-types";
import { filterByWildcard } from "@/lib/wildcard-match";

// The recurring fetch, trigger and pending lifecycle live in `useRecurringJobs` (shared with the details page,
// sc-114201); these stay exported from here for the page suite.
export { POLL_INTERVAL_MS, STALE_REQUEST_MS } from "@/hooks/use-recurring-jobs";
export const RECURRING_DEFAULT_SORT: SortingState = [{ id: "id", desc: true }];

export function JobsPage() {
  const { data, isLoading, triggering, pending, refresh, triggerJob } = useRecurringJobs();
  const [sorting, setSorting] = useState<SortingState>(RECURRING_DEFAULT_SORT);
  // The type-to-filter pattern (sc-114202): client-side over the fetched rows, never written to the URL.
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();

  const runNow = useCallback(
    (job: RecurringJob) => {
      void triggerJob(job.id);
    },
    [triggerJob]
  );

  usePollWhile({
    enabled: shouldPoll(data, pending.size),
    intervalMs: POLL_INTERVAL_MS,
    onTick: refresh,
  });

  const columns = useMemo(
    () =>
      getRecurringColumns({
        onRunNow: runNow,
        isTriggering: (id) => triggering.has(id),
        isPending: (id) => pending.has(id),
      }),
    [runNow, triggering, pending]
  );

  // The filter narrows the fetched rows by job id; polling above keeps reading `data`, so a hidden
  // in-flight row still keeps the grid polling.
  const filtered = useMemo(() => filterByWildcard(data, filter, (job) => [job.id]), [data, filter]);

  // Normalized base order, so TanStack's `rowA.index - rowB.index` tie-break IS the job id and a poll
  // that returns the same rows in a different storage order cannot reshuffle the grid.
  const rows = useMemo(() => orderByKey(filtered, (job) => job.id), [filtered]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...SORTABLE_GRID_OPTIONS,
    getRowId: (job) => job.id,
    state: { sorting },
    onSortingChange: setSorting,
  });

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Recurring Jobs"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Recurring Jobs" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Recurring Jobs"
          description="Scheduled jobs registered on conduit-api"
          actions={<TriggerDialog onTrigger={triggerJob} />}
        />

        <DataTableWrapper
          title="Schedules"
          description="Registered recurring-job definitions"
          isLoading={isLoading}
          toolbar={
            <GridFilterInput
              value={filter}
              onChange={setFilter}
              label="Filter recurring jobs"
              placeholder="Filter by job id (* = any text)"
            />
          }
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHead key={header.id} header={header} />
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  // A row click opens the job's details page (sc-114201). The Job cell's link and the Run
                  // now button stop propagation, so neither also fires this.
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => void navigate(jobDetailsPath(row.original.id))}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {data.length > 0 ? NO_MATCHES_TEXT : "No recurring jobs registered."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableWrapper>
      </div>
    </div>
  );
}
